import {Module} from 'vuex'
import {Theme} from '@/types/themes'
import {READER_ADVANCED_DEFAULTS} from '@/types/reader-settings'
import {clampColumns, GRID_COLUMNS_AUTO, GridColumnsOverride, GridScope} from '@/functions/grid-utilities'
import {UI_DEFAULTS, UiSettings} from '@/types/ui-settings'

export const persistedModule: Module<any, any> = {
  state: {
    locale: '',
    // Tsuki is dark-first: default to dark instead of following the OS.
    // Users can still pick "System" in the sidebar.
    theme: Theme.DARK,
    webreader: {
      paged: {
        scale: '',
        pageLayout: '',
      },
      continuous: {
        scale: '',
        padding: '',
        margin: '',
      },
      readingDirection: '',
      // Tsuki: on by default — swiping to turn the page is the expected gesture on a
      // touch screen, and it only ever fires from touch input, so desktop is unaffected
      swipe: true,
      alwaysFullscreen: false,
      animations: true,
      background: '',
      // Tsuki's extra reader settings — see types/reader-settings.ts
      advanced: {...READER_ADVANCED_DEFAULTS},
    },
    epubreader: {},
    browsingPageSize: undefined as unknown as number,
    thumbnailsPageSize: undefined as unknown as number,
    collection: {
      filter: {},
    },
    readList: {
      filter: {},
    },
    library: {
      // DEPRECATED: this is the old filter, before criteria-dsl was introduced
      filter: {},
      // this is the criteria-dsl filter, incompatible with the previous one
      filterDsl: {},
      filterMode: {},
      sort: {},
      filterDslBooks: {},
      filterModeBooks: {},
      sortBooks: {},
      route: {},
    },
    importPath: '',
    duplicatesNewPageSize: 10,
    rememberMe: false,
    // Tsuki: sidebar collapsed to an icon rail
    sidebarMini: false,
    // Tsuki: pinned cards-per-row, null = derive it from the available width.
    // Separate values for narrow and wide layouts; client-side only, never sent to the server.
    gridColumns: {...GRID_COLUMNS_AUTO} as GridColumnsOverride,
    /**
     * Tsuki's client-side interface settings. Deliberately not client-settings on the server:
     * these are per-device preferences (how big the cards are on *this* screen), and they must
     * work before the settings request comes back.
     */
    ui: {...UI_DEFAULTS} as UiSettings,
  },
  getters: {
    getLocaleFirstDay: (state) => () => {
      try {
        // @ts-ignore
        const loc = new Intl.Locale(state.locale)
        try {
          // @ts-ignore
          return loc.getWeekInfo().firstDay
        } catch (e) {
        }
        try {
          // @ts-ignore
          return loc.weekInfo.firstDay
        } catch (e) {
        }
      } catch (e) {
      }
      return 1
    },
    getCollectionFilter: (state) => (id: string) => {
      return state.collection.filter[id]
    },
    getReadListFilter: (state) => (id: string) => {
      return state.readList.filter[id]
    },
    getLibraryFilter: (state) => (id: string) => {
      return state.library.filterDsl[id]
    },
    getLibraryFilterBooks: (state) => (id: string) => {
      return state.library.filterDslBooks[id]
    },
    getLibraryFilterMode: (state) => (id: string) => {
      return state.library.filterMode[id]
    },
    getLibraryFilterModeBooks: (state) => (id: string) => {
      return state.library.filterModeBooks[id]
    },
    getLibrarySort: (state) => (id: string) => {
      return state.library.sort[id]
    },
    getLibrarySortBooks: (state) => (id: string) => {
      return state.library.sortBooks[id]
    },
    getLibraryRoute: (state) => (id: string) => {
      return state.library.route[id]
    },
    /**
     * Reader setting with a fallback, so states persisted before a setting existed still work.
     *
     * The test is against `undefined` rather than `key in stored`: a key that is present but
     * holds nothing should fall back too. `false` and `0` are real values and survive either way.
     */
    getReaderAdvanced: (state) => (key: string) => {
      const stored = state.webreader.advanced || {}
      return stored[key] === undefined ? (READER_ADVANCED_DEFAULTS as any)[key] : stored[key]
    },
    // falls back to auto so state persisted before this setting existed still works
    getGridColumns: (state) => (scope: GridScope): number | null => {
      const val = (state.gridColumns || {})[scope]
      return val == null ? null : clampColumns(Number(val))
    },
    /** Any UI setting, with its default — persisted states written before a key existed still work. */
    getUi: (state) => (key: keyof UiSettings): any => {
      const stored = state.ui || {}
      return key in stored ? stored[key] : UI_DEFAULTS[key]
    },
    getBlurKeepFirst: (state): boolean => {
      const stored = state.ui || {}
      return 'blurKeepFirst' in stored ? stored.blurKeepFirst : UI_DEFAULTS.blurKeepFirst
    },
  },
  mutations: {
    setLocale(state, val) {
      state.locale = val
    },
    setTheme(state, val) {
      state.theme = val
    },
    setWebreaderPagedScale(state, val) {
      state.webreader.paged.scale = val
    },
    setWebreaderPagedPageLayout(state, val) {
      state.webreader.paged.pageLayout = val
    },
    setWebreaderContinuousScale(state, val) {
      state.webreader.continuous.scale = val
    },
    setWebreaderContinuousPadding(state, val) {
      state.webreader.continuous.padding = val
    },
    setWebreaderContinuousMargin(state, val) {
      state.webreader.continuous.margin = val
    },
    setWebreaderReadingDirection(state, val) {
      state.webreader.readingDirection = val
    },
    setWebreaderSwipe(state, val) {
      state.webreader.swipe = val
    },
    setWebreaderAlwaysFullscreen(state, val) {
      state.webreader.alwaysFullscreen = val
    },
    setWebreaderAnimations(state, val) {
      state.webreader.animations = val
    },
    setWebreaderBackground(state, val) {
      state.webreader.background = val
    },
    /**
     * One mutation for all of Tsuki's extra reader settings, so adding a setting
     * does not mean adding a mutation. Replaces the object so Vue 2 picks up keys
     * that were absent from an older persisted state.
     */
    setWebreaderAdvanced(state, {key, value}: { key: string, value: any }) {
      state.webreader.advanced = {
        ...READER_ADVANCED_DEFAULTS,
        ...state.webreader.advanced,
        [key]: value,
      }
    },
    resetWebreaderAdvanced(state) {
      state.webreader.advanced = {...READER_ADVANCED_DEFAULTS}
    },
    setEpubreaderSettings(state, val) {
      state.epubreader = val
    },
    setBrowsingPageSize(state, val) {
      state.browsingPageSize = val
    },
    setThumbnailsPageSize(state, val) {
      state.thumbnailsPageSize = val
    },
    setCollectionFilter(state, {id, filter}) {
      state.collection.filter[id] = filter
    },
    setReadListFilter(state, {id, filter}) {
      state.readList.filter[id] = filter
    },
    setLibraryFilter(state, {id, filter}) {
      state.library.filterDsl[id] = filter
    },
    setLibraryFilterBooks(state, {id, filter}) {
      state.library.filterDslBooks[id] = filter
    },
    setLibraryFilterMode(state, {id, filterMode: filterMode}) {
      state.library.filterMode[id] = filterMode
    },
    setLibraryFilterModeBooks(state, {id, filterMode: filterMode}) {
      state.library.filterModeBooks[id] = filterMode
    },
    setLibrarySort(state, {id, sort}) {
      state.library.sort[id] = sort
    },
    setLibrarySortBooks(state, {id, sort}) {
      state.library.sortBooks[id] = sort
    },
    setLibraryRoute(state, {id, route}) {
      state.library.route[id] = route
    },
    setImportPath(state, val) {
      state.importPath = val ?? ''
    },
    setDuplicatesNewPageSize(state, val) {
      state.duplicatesNewPageSize = val
    },
    setRememberMe(state, val) {
      state.rememberMe = val
    },
    setSidebarMini(state, val) {
      state.sidebarMini = val
    },
    /** One mutation for every UI setting — adding one must not mean adding a mutation. */
    setUi(state, {key, value}: { key: string, value: any }) {
      state.ui = {...UI_DEFAULTS, ...state.ui, [key]: value}
    },
    resetUi(state) {
      state.ui = {...UI_DEFAULTS}
    },
    setGridColumns(state, {scope, columns}: { scope: GridScope, columns: number | null }) {
      // clamped on write so a hand-edited localStorage cannot break every grid in the app
      const next = {...GRID_COLUMNS_AUTO, ...(state.gridColumns || {})}
      next[scope] = columns == null ? null : clampColumns(Number(columns))
      state.gridColumns = next
    },
  },
}
